import app from 'app';
import { UNCATEGORIZED_FLAG } from '#/const.ts';

// Groups a flat, server-ordered preview list into version families WITHOUT
// reordering it (the pagination cursor reads the last element's lastUpdate).
// The family key is `folderId || id`: children carry folderId = the root's id,
// while the root itself has folderId null and is keyed by its own id, so root
// and children collapse into one group. An entry is a family
// { key, folderId, members } when it has >1 member or any member is a known
// child (folderId set); otherwise a standalone { key, story }. Members arriving
// on later pages merge into their first-seen anchor.
function buildStoryGroups(stories) {
  const byKey = new Map();
  const order = [];

  stories.forEach((story) => {
    const key = story.folderId || story.id;
    let group = byKey.get(key);
    if (!group) {
      group = { key, members: [] };
      byKey.set(key, group);
      order.push(group);
    }
    group.members.push(story);
  });

  return order.map((group) => {
    const isFamily =
      group.members.length > 1 || group.members.some((m) => !!m.folderId);
    if (!isFamily) {
      return { key: group.members[0].id, story: group.members[0] };
    }
    return {
      key: 'folder:' + group.key,
      folderId: group.key,
      members: group.members,
    };
  });
}

export default {
  state() {
    const defaultStoriesFilters = {
      programmerId: null,
      brandId: null,
      q: '',
    };

    const defaultVideosFilters = {
      programmerId: null,
      brandId: null,
      q: '',
      type: 'ALL',
    };

    const defaultReadyMadeVideosFilters = {
      q: '',
      voiceOver: null,
      category: '',
    };

    const defaultStoryboardsFilters = {
      q: '',
      categoryIds: [],
      aspectRatios: [],
    };

    const defaultMyWork = {
      stories: false,
      videos: false,
    };

    return {
      // In Progress
      stories: [],
      storiesFilters: _.cloneDeep(defaultStoriesFilters),
      defaultStoriesFilters,
      storiesPage: {
        noMore: false,
        lastModifiedDate: null,
      },

      selectedStory: null,
      selectedStoryTemplate: null,

      // Produced
      videos: [],
      videosFilters: _.cloneDeep(defaultVideosFilters),
      defaultVideosFilters,
      videosPage: {
        noMore: false,
        page: 0,
      },

      // Discover
      readyMadeVideos: [],
      readyMadeVideosFilters: _.cloneDeep(defaultReadyMadeVideosFilters),
      defaultReadyMadeVideosFilters,
      storyboardsFilters: _.cloneDeep(defaultStoryboardsFilters),
      defaultStoryboardsFilters,
      tabFiltersMemory: {
        /** @type null | 'wochit' | 'my' */
        recentTab: null,
        wochitTemplates: {
          storyboardsFilters: _.cloneDeep(defaultStoryboardsFilters),
        },
        myTemplates: {
          storyboardsFilters: _.cloneDeep(defaultStoryboardsFilters),
          storyboardBrands: [],
        },
      },
      storyboardBrands: [],
      readyMadeProgrammers: [],
      readyMadeCategories: [],
      readyMadeVideosPage: {
        noMore: false,
        page: 0,
      },
      storyboards: [],
      // for story template filters use readyMadeVideosFilters
      storyboardsPage: {
        indexOfSelectedStoryboard: -1,
        indexOfSelectedMyTeamStoryboard: -1,
        noMore: false,
        page: 0,
      },

      // Home
      homeStories: [],
      homeStoryboards: [],

      /** persistable cross dashboard myWork states */
      myWork: _.cloneDeep(defaultMyWork),

      /** control visibility */
      show: {
        familiarWithHomepage: !!app.stash.get('familiarWithHomepage'),
      },

      /** control fetching states */
      fetching: {
        stories: false,
        videos: false,
        readyMadeVideos: false,
        storyboards: { value: false, reset: true },
        homeStories: false,
        homeStoryboards: false,
      },

      /** control empty states */
      hasEmpty: {
        readyMadeVideos: false,
        storyboards: false,
        stories: true,
        videos: true,
        homeStories: true,
        homeStoryboards: true,
      },

      galleryItem: {
        width: 0,
        height: 0,
        numberInRow: 0,
        padding: 0,
        widthSmaller: 0,
      },

      /** control locking state */
      lock: {
        /**
         * auto refresh dashboard lists for wochit user
         * false - allow; true - disable (temporarily)
         */
        autorefresh: false,
      },
    };
  },

  // @params state, getters
  getters: {
    indexOfSelectedStoryboard: (state) =>
      state.storyboardsPage.indexOfSelectedStoryboard,
    indexOfSelectedMyTeamStoryboard: (state) =>
      state.storyboardsPage.indexOfSelectedMyTeamStoryboard,
    selectedStory: (state) => state.selectedStory,
    show: (state) => state.show,
    fetching: (state) => state.fetching,
    stories: (state) => state.stories,
    homeStories: (state) => state.homeStories,
    // In Progress + Home grids: version families collapsed to one entry each.
    // See buildStoryGroups above for the grouping rules.
    groupedStories: (state) => buildStoryGroups(state.stories),
    groupedHomeStories: (state) => buildStoryGroups(state.homeStories),
    storiesFilters: (state) => state.storiesFilters,
    videos: (state) => state.videos,
    readyMadeVideos: (state) => state.readyMadeVideos,
    videosFilters: (state) => state.videosFilters,
    homeStoryboards: (state) => state.homeStoryboards,
    hasDefaultStoriesFilters: (state) => {
      return _.isEqual(state.storiesFilters, state.defaultStoriesFilters);
    },
    hasDefaultVideosFilters: (state) => {
      return _.isEqual(state.videosFilters, state.defaultVideosFilters);
    },
  },

  // @params state, payload
  mutations: {
    setGalleryItemDetails(state, details) {
      state.galleryItem.width = details.width;
      state.galleryItem.widthSmaller = details.width - 20;
      state.galleryItem.height = details.height;
      state.galleryItem.numberInRow = +details.numberInRow;
      state.galleryItem.padding = details.padding;
    },

    setIndexOfSelectedStoryboard(state, index) {
      state.storyboardsPage.indexOfSelectedStoryboard = index;
    },
    setIndexOfSelectedMyTeamStoryboard(state, index) {
      state.storyboardsPage.indexOfSelectedMyTeamStoryboard = index;
    },

    emptyFetchResults(state) {
      state.stories = [];
      state.videos = [];
      state.homeStories = [];
      state.homeStoryboards = [];
    },

    resetStoriesFilters(state) {
      app.Vue.set(
        state,
        'storiesFilters',
        _.cloneDeep(state.defaultStoriesFilters)
      );
    },

    resetVideosFilters(state) {
      app.Vue.set(
        state,
        'videosFilters',
        _.cloneDeep(state.defaultVideosFilters)
      );
    },

    deleteStory(state, id) {
      state.homeStories = state.homeStories.filter((item) => {
        return id !== item.id;
      });
      state.hasEmpty.homeStories = _.isEmpty(state.homeStories);

      state.stories = state.stories.filter((item) => {
        return id !== item.id;
      });
      state.hasEmpty.stories = _.isEmpty(state.stories);
    },

    // Apply server-truth lineage fields onto already-fetched rows (after a
    // branch/delete reshapes a group). `updates` is [{ id, folderId, parentId,
    // versionNumber }]; never invent these client-side.
    syncStoryLineage(state, updates) {
      const applyTo = (list) => {
        updates.forEach((u) => {
          const story = list.find((item) => item.id === u.id);
          if (story) {
            app.Vue.set(story, 'folderId', u.folderId);
            app.Vue.set(story, 'parentId', u.parentId);
            app.Vue.set(story, 'versionNumber', u.versionNumber);
          }
        });
      };
      applyTo(state.stories);
      applyTo(state.homeStories);
    },

    // Head-insert a freshly created draft preview. Cursor-safe: the pagination
    // cursor reads the LAST element, so unshifting never disturbs it.
    upsertStoryPreviewFirst(state, preview) {
      if (!state.stories.some((item) => item.id === preview.id)) {
        state.stories.unshift(preview);
        state.hasEmpty.stories = false;
      }
      if (!state.homeStories.some((item) => item.id === preview.id)) {
        state.homeStories.unshift(preview);
        state.hasEmpty.homeStories = false;
      }
    },

    deleteVideo(state, id) {
      state.homeStoryboards = state.homeStoryboards.filter((item) => {
        return id !== item.id;
      });
      state.hasEmpty.homeStoryboards = _.isEmpty(state.homeStoryboards);

      state.videos = state.videos.filter((item) => {
        return id !== item.id;
      });
      state.hasEmpty.videos = _.isEmpty(state.videos);
    },

    deleteTemplate(state, id) {
      state.storyboards = state.storyboards.filter((item) => {
        return id !== item.id;
      });
      state.hasEmpty.storyboards = _.isEmpty(state.storyboards);
    },

    setSelectedStory(state, id) {
      state.selectedStory = id;
    },

    // Template Gallery mutations
    setStoryTemplates(state, payload) {
      state.storyboards = payload.storyboards;
    },

    setStoryboardBrands(state, { brands }) {
      _.each(brands, (p) => Object.assign(p, { selected: true }));

      const sorted = brands.sort(function ascendingLabelUppercase(a, b) {
        const la = a.label && a.label.toUpperCase();
        const lb = b.label && b.label.toUpperCase();
        return la < lb ? -1 : la > lb ? 1 : 0;
      });

      state.storyboardBrands = sorted;
    },

    setReadyMadeProgrammers(state, { fromReadyMade }) {
      _.each(fromReadyMade, (p) => Object.assign(p, { selected: true }));

      const sorted = fromReadyMade.sort(function ascendingLabelUppercase(a, b) {
        const la = a.label && a.label.toUpperCase();
        const lb = b.label && b.label.toUpperCase();
        return la < lb ? -1 : la > lb ? 1 : 0;
      });

      state.readyMadeProgrammers = sorted;
    },

    setReadyMadeCategories(state, { categories }) {
      state.readyMadeCategories = categories;
    },

    updateStoryboard(state, payload) {
      const updateStoryboards = [...state.storyboards].filter(
        (temp) => temp.id === payload.storyId
      );
      const keys = Object.keys(payload);
      for (const sb of updateStoryboards) {
        for (const key of keys) {
          if (key !== 'storyId') {
            app.Vue.set(sb, key, payload[key]);
          }
        }
      }
    },

    resetStoryboardFilters(state) {
      state.storyboardsFilters = _.cloneDeep(state.defaultStoryboardsFilters);
    },

    /**
     * manage multi-selection
     * @param state
     * @param id {string|null} - category id
     * @param multipleSelection {boolean} - if user held meta or control
     */
    setStoryboardsFiltersCategories(state, { id, multipleSelection }) {
      if (!id) {
        state.storyboardsFilters.categoryIds = [];
      } else if (id === UNCATEGORIZED_FLAG) {
        state.storyboardsFilters.categoryIds = [UNCATEGORIZED_FLAG];
      } else {
        if (multipleSelection) {
          const exists = state.storyboardsFilters.categoryIds.includes(id);

          if (exists) {
            state.storyboardsFilters.categoryIds =
              state.storyboardsFilters.categoryIds.filter((_id) => _id !== id);
          } else {
            state.storyboardsFilters.categoryIds.push(id);
          }

          state.storyboardsFilters.categoryIds =
            state.storyboardsFilters.categoryIds.filter(
              (_id) => _id !== UNCATEGORIZED_FLAG
            );
        } else {
          state.storyboardsFilters.categoryIds = [id];
        }
      }
    },

    setStoryboardsFiltersAspectRatios(state, { ar }) {
      const { aspectRatios } = state.storyboardsFilters;
      const arIndex = aspectRatios.indexOf(ar);

      if (arIndex >= 0) {
        aspectRatios.splice(arIndex, 1);
      } else {
        aspectRatios.push(ar);
      }
    },

    unlockStory(state, story) {
      app.Vue.set(story, 'locked', false);
      app.Vue.set(story, 'lockedUserName', null);
    },

    updateStoryThumbnail(state, { story, thumbnailPath }) {
      app.Vue.set(story, 'thumbnailPath', thumbnailPath);
    },

    updateStoryTitle(state, { story, storyTitle }) {
      app.Vue.set(story, 'title', storyTitle);
    },
  },

  // @param context {{state, rootState, commit, dispatch, getters, rootGetters}}
  // @param payload
  actions: {
    fetchHomeStories({ state }, options) {
      options = _.options(options, {
        bReset: true,
        size: 3 * app.DASHBOARD_HOME_MAX_RESULTS_PER_ROW,
      });

      if (!options.bReset && state.fetching.homeStories) {
        return;
      }

      state.fetching.homeStories = true;

      options.bReset && app.api.draftStoryPreviews.getAllAbort();
      return app.api.draftStoryPreviews
        .getAll({
          size: options.size,
          myWork: state.myWork.stories,
        })
        .then(
          ({ stories }) => {
            state.homeStories = stories;
            state.fetching.homeStories = false;
            state.hasEmpty.homeStories = _.isEmpty(state.homeStories);
          },
          () => {
            state.fetching.homeStories = false;
          }
        );
    },

    fetchStories({ state, rootGetters }, options) {
      options = Object.freeze(
        Object.assign(
          {
            bReset: true,
            bFetchMore: false,
          },
          options
        )
      );

      if (
        (!options.bReset && state.fetching.stories) ||
        (options.bFetchMore && state.storiesPage.noMore)
      ) {
        return;
      }

      state.fetching.stories = true;

      if (!options.bFetchMore) {
        state.stories = [];
        state.storiesPage.noMore = false;
      }

      state.storiesPage.lastModifiedDate =
        options.bFetchMore && !_.isEmpty(state.stories)
          ? _.last(state.stories).lastUpdate
          : null;
      const lastModifiedDate = state.storiesPage.lastModifiedDate;

      const payload = Object.assign({}, state.storiesFilters, {
        lastModifiedDate,
        myWork: state.myWork.stories,
      });
      if (rootGetters['user/isImpededTemplatizer']) {
        payload.template = true;
      }

      options.bReset && app.api.draftStoryPreviews.getAllAbort();
      return app.api.draftStoryPreviews.getAll(payload).then(
        ({ stories, noMore }) => {
          state.storiesPage.noMore = noMore;
          if (options.bFetchMore) {
            state.stories = state.stories.concat(stories);
          } else {
            state.stories = stories;
          }

          state.fetching.stories = false;
          state.hasEmpty.stories = _.isEmpty(state.stories);
        },
        () => {
          state.fetching.stories = false;
        }
      );
    },

    fetchVideos({ state }, options) {
      options = Object.freeze(
        Object.assign(
          {
            bReset: true,
            bFetchMore: false,
          },
          options
        )
      );

      if (
        (!options.bReset && state.fetching.videos) ||
        (options.bFetchMore && state.videosPage.noMore)
      ) {
        return;
      }

      state.fetching.videos = true;

      if (!options.bFetchMore) {
        state.videos = [];
        state.videosPage.noMore = false;
      }

      const page = options.bFetchMore ? state.videosPage.page + 1 : 0;

      options.bReset && app.api.videos.abort();

      return app.api.producedStories
        .get(
          Object.assign({}, state.videosFilters, {
            page,
            myWork: state.myWork.videos,
          })
        )
        .then(
          ({ videos, noMore }) => {
            state.videosPage.noMore = noMore;

            if (options.bFetchMore) {
              state.videos = state.videos.concat(videos);
            } else {
              state.videos = videos;
            }

            state.videosPage.page = page;
            state.fetching.videos = false;
            state.hasEmpty.videos = _.isEmpty(state.videos);
          },
          () => {
            state.fetching.videos = false;
          }
        );
    },

    fetchReadyMadeProgrammers({ state, commit }) {
      const rv = new _.ColdPromise();
      if (_.isEmpty(state.readyMadeProgrammers)) {
        app.api.allowedProgrammers
          .getProgrammersForReadyMadeVideos()
          .then(({ programmers, categories }) => {
            commit('setReadyMadeProgrammers', {
              fromReadyMade: programmers,
            });
            commit('setReadyMadeCategories', {
              categories,
            });
            rv.resolve();
          });
      } else {
        rv.resolve();
      }
      return rv;
    },

    INIT_STORYBOARD_BRANDS({ state, commit, rootGetters }) {
      return new Promise((resolve) => {
        if (_.isEmpty(state.storyboardBrands)) {
          const storyboardBrands = rootGetters[
            'user/selectedOrganization'
          ].brands.map((brand) => ({
            id: brand._mixedId,
            label: brand.name,
            brandId: brand.brandId,
            programmerId: brand.programmerId,
          }));

          commit('setStoryboardBrands', { brands: storyboardBrands });
          resolve();
        } else {
          resolve();
        }
      });
    },

    fetchReadyMadeVideos({ state }, options) {
      options = _.options(options, {
        bReset: true,
        bFetchMore: false,
      });

      if (
        _.isEmpty(state.readyMadeProgrammers) ||
        (!options.bReset && state.fetching.readyMadeVideos) ||
        (options.bFetchMore && state.readyMadeVideosPage.noMore)
      ) {
        return;
      }

      state.fetching.readyMadeVideos = true;

      if (!options.bFetchMore) {
        state.readyMadeVideos = [];
        state.readyMadeVideosPage.noMore = false;
      }

      const page = options.bFetchMore ? state.readyMadeVideosPage.page + 1 : 0;

      const programmerIds = _.filter(
        state.readyMadeProgrammers,
        (p) => p.selected && !p._fromMarketplace
      )
        .map((p) => p.id)
        .join(',');
      const marketplaceProgrammerIds = _.filter(
        state.readyMadeProgrammers,
        (p) => p.selected && p._fromMarketplace
      )
        .map((p) => p.id)
        .join(',');

      const filters = Object.assign({}, state.readyMadeVideosFilters, {
        page,
        programmerIds,
        marketplaceProgrammerIds,
      });

      options.bReset && app.api.readyMadeVideos.abort();
      const cp = app.api.readyMadeVideos.get(filters);
      cp.then(({ videos, noMore }) => {
        state.readyMadeVideosPage.noMore = noMore;

        if (options.bFetchMore) {
          state.readyMadeVideos = state.readyMadeVideos.concat(videos);
        } else {
          state.readyMadeVideos = videos;
        }

        state.readyMadeVideosPage.page = page;
        state.hasEmpty.readyMadeVideos = _.isEmpty(state.readyMadeVideos);
      });
      cp.finally(() => {
        state.fetching.readyMadeVideos = false;
      });
      return cp;
    },

    fetchHomeStoryboards({ state, dispatch }, options) {
      state.fetching.homeStoryboards = true;

      dispatch('INIT_STORYBOARD_BRANDS').then(() => {
        options = _.options(options, {
          bReset: true,
          size: 3 * app.DASHBOARD_HOME_MAX_RESULTS_PER_ROW,
        });

        if (_.isEmpty(state.storyboardBrands)) {
          return;
        }

        const sharedBrands = _.filter(
          state.storyboardBrands,
          (b) => b.brandId !== -1
        );
        const payload = {
          q: state.storyboardsFilters.q,
          categoryIds:
            state.storyboardsFilters.categoryIds.join(',') || undefined,
          aspectRatios:
            state.storyboardsFilters.aspectRatios.length > 0
              ? state.storyboardsFilters.aspectRatios.join(',')
              : undefined,
          page: 0,
          sharedProgrammerIds: _.uniq(
            sharedBrands.map((b) => b.programmerId)
          ).join(','),
          brandIds: sharedBrands.map((b) => b.brandId).join(','),
        };

        app.api.producedStoryTemplates.abortGetPreferred();
        const p1 = app.api.producedStoryTemplates
          .getPreferred(payload)
          .then(({ templates }) => {
            app.Vue.set(state, 'homeStoryboards', templates);
            state.hasEmpty.homeStoryboards = _.isEmpty(state.homeStoryboards);
          });

        return p1.finally(() => {
          state.fetching.homeStoryboards = false;
        });
      });
    },

    postAiPrompt(
      { state },
      {
        videoPrompt,
        language,
        visualType,
        programmerId,
        brandId,
        themeId,
        aspectRatio,
        voiceId,
      }
    ) {
      return app.api.draftStories.createNewAiStory(
        {
          videoPrompt,
          language,
          visualType,
          programmerId,
          brandId,
          themeId,
          aspectRatio,
          voiceId,
        },
        { showError: true }
      );
    },

    aiApiRequest(
      { state },
      {
        videoPrompt,
        language,
        visualType,
        programmerId,
        brandId,
        themeId,
        voiceId,
        storyId,
        assetUUIDs,
        regenerateStory,
        aspectRatio,
      }
    ) {
      return app.api.draftStories.createAiStoryAiAPI(
        {
          message: videoPrompt,
          language,
          visualType,
          programmerId,
          brandId,
          themeId,
          voiceId,
          storyId,
          assetUUIDs,
          regenerateStory,
          aspectRatio,
        },
        { showError: true }
      );
    },

    fetchStoryboardsCategoryTypeCount({ state, rootState }, options) {
      return new Promise((resolve, reject) => {
        app.api.producedStoryTemplates
          .getAiTemplatesCount()
          .then(({ numOfAiTemplates }) => {
            if (numOfAiTemplates > 0) {
              resolve(numOfAiTemplates);
            } else {
              reject();
            }
          })
          .catch((error) => {
            reject(error);
          });
      });
    },

    fetchStoryboards({ state, rootState }, options) {
      options = _.options(options, {
        wochitTemplates: false,
        fetchMore: false,
      });

      if (
        _.isEmpty(state.storyboardBrands) ||
        (options.fetchMore && state.storyboardsPage.noMore) ||
        (options.fetchMore && state.fetching.storyboards.value)
      ) {
        return;
      }

      state.fetching.storyboards.value = true;

      if (!options.fetchMore) {
        state.storyboards = [];
        state.storyboardsPage.noMore = false;
      }

      const allBrands = _.filter(
        state.storyboardBrands,
        (b) => b.brandId !== -1
      );
      const allSelectedBrands = _.filter(allBrands, (b) => b.selected);
      const categoryIds = state.storyboardsFilters.categoryIds;
      const uncategorizedCategories = categoryIds.includes(UNCATEGORIZED_FLAG);
      const shouldSendBrands =
        !options.wochitTemplates &&
        allSelectedBrands.length &&
        allSelectedBrands.length !== allBrands.length;
      const shouldSendCategories =
        !uncategorizedCategories && categoryIds.length;
      const shouldSendAR = state.storyboardsFilters.aspectRatios.length > 0;

      const payload = {
        q: state.storyboardsFilters.q,
        searchForUnCategorized: uncategorizedCategories ? true : undefined,
        categoryIds: shouldSendCategories ? categoryIds.join(',') : undefined,
        aspectRatios: shouldSendAR
          ? state.storyboardsFilters.aspectRatios.join(',')
          : undefined,
        page: options.fetchMore ? state.storyboardsPage.page + 1 : 0,
        sharedProgrammerIds: options.wochitTemplates
          ? undefined
          : _.uniq(allSelectedBrands.map((b) => b.programmerId)).join(','),
        brandIds: shouldSendBrands
          ? allSelectedBrands.map((b) => b.brandId).join(',')
          : undefined,
        channelId: options.wochitTemplates ? tk.context.channel.id : undefined,
      };

      !options.fetchMore && app.api.producedStoryTemplates.abortGetAll();
      app.api.producedStoryTemplates
        .getAll(payload)
        .then(({ templates, noMore }) => {
          app.Vue.set(
            state,
            'storyboards',
            options.fetchMore ? state.storyboards.concat(templates) : templates
          );

          state.storyboardsPage.noMore = noMore;
          state.storyboardsPage.page = payload.page;
          state.hasEmpty.storyboards = _.isEmpty(state.storyboards);
        })
        .finally(() => {
          state.fetching.storyboards.value = false;
        });
    },

    CREATE_STORYBOARD({ commit }, payload) {
      return app.api.draftStoryTemplates
        .createNew({
          sharedProgrammerIds: app.programmers.toArray().map((p) => p.id),
          ...payload,
        })
        .then(
          (storyId) => {
            if (storyId) {
              app.navigate(`/stories/${storyId}`, { trigger: false });
              app.controller.routeStories(storyId);
            }
          },
          (error) => {
            app.doctorController.report('CREATE_STORYBOARD Failed', error);
          }
        );
    },

    UPDATE_STORYBOARD({ commit, rootState, rootGetters }, payload) {
      const contextCategories = rootGetters['user/isImpededTemplatizer']
        ? rootState.channelOwnedCategories
        : rootState.storyboardCategories;
      let categories = [];

      if (payload.categoryIds) {
        // sometimes the set of available categories change. We need to filter out categories saved on the storyboard
        // that are not part of the available categories set
        payload.categoryIds = payload.categoryIds?.filter((id) =>
          contextCategories.find((c) => c.id === id)
        );

        categories = payload.categoryIds.map((catId) => {
          const { id, categoryName } = contextCategories.find(
            ({ id }) => id === catId
          );
          return {
            id,
            name: categoryName,
          };
        });
      }

      commit('updateStoryboard', {
        ...payload,
        categories,
      });
      app.api.producedStoryTemplates.editMetadata(payload);
    },

    EDIT_STORYBOARD(state, { id }) {
      return new Promise((resolve) => {
        app.api.producedStories.moveToDraft(id).then(
          () => {
            resolve();
          },
          (error) => {
            resolve(error);
          }
        );
      });
    },

    SET_DEFAULT_STORYBOARD_FILTERS({ state, commit }, { myTemplatesTab }) {
      if (!state.tabFiltersMemory.recentTab) {
        commit('setStoryboardBrands', { brands: state.storyboardBrands });
        commit('resetStoryboardFilters');
        state.tabFiltersMemory.recentTab = myTemplatesTab ? 'my' : 'wochit';
      } else if (state.tabFiltersMemory.recentTab === 'wochit') {
        // wochit tab
        state.tabFiltersMemory.wochitTemplates.storyboardsFilters = _.cloneDeep(
          state.storyboardsFilters
        );

        if (myTemplatesTab) {
          // going to my tab
          state.storyboardsFilters = _.cloneDeep(
            state.tabFiltersMemory.myTemplates.storyboardsFilters
          );
          state.storyboardBrands = _.cloneDeep(
            state.tabFiltersMemory.myTemplates.storyboardBrands
          );
          state.tabFiltersMemory.recentTab = 'my';
        }
      } else if (state.tabFiltersMemory.recentTab === 'my') {
        // my tab
        state.tabFiltersMemory.myTemplates.storyboardsFilters = _.cloneDeep(
          state.storyboardsFilters
        );
        state.tabFiltersMemory.myTemplates.storyboardBrands = _.cloneDeep(
          state.storyboardBrands
        );

        if (!myTemplatesTab) {
          state.storyboardsFilters = _.cloneDeep(
            state.tabFiltersMemory.wochitTemplates.storyboardsFilters
          );
          state.tabFiltersMemory.recentTab = 'wochit';
        }
      }
    },

    AI_DRAFT_READY(
      { state, commit, dispatch, rootGetters },
      { storyId, sourceStoryId, preview, thumbnailPath, storyTitle }
    ) {
      // Check both stories arrays (In Progress and Home pages)
      const story =
        state.stories.find((s) => s.id === storyId) ||
        state.homeStories.find((s) => s.id === storyId);

      if (story) {
        // Existing row (from-scratch AI wizard patches the source in place).
        commit('unlockStory', story);

        if (thumbnailPath) {
          commit('updateStoryThumbnail', { story, thumbnailPath });
        }

        if (storyTitle) {
          commit('updateStoryTitle', { story, storyTitle });
        }
      } else if (preview) {
        // Edit-with-AI created a NEW version - a new row. Insert it at the head.
        commit('upsertStoryPreviewFirst', preview);
      }

      // Unlock the source story (freed for the AI job) if it is on screen.
      if (sourceStoryId && sourceStoryId !== storyId) {
        const source =
          state.stories.find((s) => s.id === sourceStoryId) ||
          state.homeStories.find((s) => s.id === sourceStoryId);
        if (source) {
          commit('unlockStory', source);
        }
      }

      // Refresh the version group so any on-screen group card/panel reflects
      // the new version and its (server-decided) active member. The server
      // moves the active pointer only at the END of a successful run, so this
      // refetch is what surfaces the new "current". Prefer the fresh preview;
      // fall back to the cached group when the preview is missing/standalone
      // (e.g. the draftStoryPreviews fetch failed).
      let folderId = preview && preview.folderId;
      let anchorId = preview && preview.id;
      if (!folderId && sourceStoryId) {
        const group =
          rootGetters['storyVersions/groupByStoryId'](sourceStoryId);
        if (group) {
          folderId = group.folderId;
          anchorId = sourceStoryId;
        }
      }
      if (folderId && anchorId) {
        dispatch(
          'storyVersions/FETCH_VERSIONS',
          { storyId: anchorId, folderId, force: true },
          { root: true }
        ).catch(() => {});
      }
    },

    AI_DRAFT_FINISHED(
      { state, commit, dispatch, rootGetters },
      { storyId, sourceStoryId }
    ) {
      // Failure path: re-unlock the story that was locked for the AI job so the
      // user can act on it again. On an Edit-with-AI job the locked row is the
      // SOURCE (sourceStoryId); the from-scratch path only has storyId.
      const findRow = (id) =>
        id &&
        (state.stories.find((s) => s.id === id) ||
          state.homeStories.find((s) => s.id === id));

      const source = findRow(sourceStoryId);
      if (source) {
        commit('unlockStory', source);
      }

      // Also cover the from-scratch case where storyId IS the locked row.
      const story = findRow(storyId);
      if (story && story !== source) {
        commit('unlockStory', story);
      }

      // A failed/aborted run never moves the active pointer, but the server-
      // side lock did flip during the run. Refresh a cached group so stale
      // lock flags clear on any open versions surface. No-op when the story
      // is not part of a (cached) version group.
      const anchorId = sourceStoryId || storyId;
      const group =
        anchorId && rootGetters['storyVersions/groupByStoryId'](anchorId);
      if (group) {
        dispatch(
          'storyVersions/FETCH_VERSIONS',
          { storyId: anchorId, folderId: group.folderId, force: true },
          { root: true }
        ).catch(() => {});
      }
    },
  },
};
